import { useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Upload, Loader2, CheckCircle2, MessageCircle, FileImage } from "lucide-react";
import { api, waLink } from "../../lib/site";
import { Reveal, Overline } from "./ui";

const FileInput = ({ label, file, onPick, required, testid }) => {
  const ref = useRef(null);
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">
        {label} {required && <span className="text-volt">*</span>}
      </label>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        data-testid={testid}
        className="flex w-full items-center gap-2 border border-dashed border-white/20 bg-ink px-4 py-3 text-sm text-white/70 transition-colors hover:border-volt hover:text-white"
      >
        {file ? <FileImage size={16} className="text-volt" /> : <Upload size={16} />}
        <span className="truncate">{file ? file.name : "Choose an image…"}</span>
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
};

export default function Verify() {
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [license, setLicense] = useState(null);
  const [govtId, setGovtId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !license) {
      toast.error("Please add your name, phone and licence photo.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("note", form.note);
      fd.append("license", license);
      if (govtId) fd.append("govt_id", govtId);
      await api.post("/verifications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch {
      toast.error("Upload failed. Please try again or send documents on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="verify" className="relative bg-ink px-5 py-24 md:px-10 md:py-32" data-testid="verify-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Overline>Skip the queue</Overline>
          <h2 className="mt-4 font-heading text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl md:text-5xl">
            Verify now, drive sooner
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/60 md:text-base">
            Upload a clear photo of your driving licence (and optionally a govt photo ID) before you arrive.
            We'll pre-check your documents so your pickup takes minutes, not longer.
          </p>
          <ul className="mt-8 space-y-3">
            {["Faster handover at pickup", "Your details stay private with us", "No online payment — deposit is paid in person"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-white/75">
                <ShieldCheck size={16} className="text-volt" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <div className="flex flex-col items-start border border-volt/30 bg-volt/[0.06] p-8" data-testid="verify-success">
              <CheckCircle2 size={40} className="text-volt" />
              <h3 className="mt-4 font-heading text-2xl font-bold uppercase tracking-tight text-white">Documents received</h3>
              <p className="mt-2 text-sm text-white/70">
                Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! We'll review your documents. Message us on WhatsApp to lock your dates.
              </p>
              <a
                href={waLink(`Hi, I've uploaded my documents for verification. Name: ${form.name}, Phone: ${form.phone}. I'd like to book a vehicle.`)}
                target="_blank"
                rel="noreferrer"
                data-testid="verify-whatsapp"
                className="mt-6 inline-flex items-center gap-2 bg-volt px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover"
              >
                <MessageCircle size={15} /> Continue on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-ink-surface p-6 md:p-8" data-testid="verify-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Full Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="verify-name" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white outline-none focus:border-volt" placeholder="As on your licence" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Phone / WhatsApp *</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="verify-phone" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white outline-none focus:border-volt" placeholder="10-digit number" />
                </div>
                <FileInput label="Driving Licence" file={license} onPick={setLicense} required testid="verify-license" />
                <FileInput label="Govt Photo ID (optional)" file={govtId} onPick={setGovtId} testid="verify-id" />
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Note (optional)</label>
                  <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="verify-note" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white outline-none focus:border-volt" placeholder="Preferred vehicle or dates" />
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="verify-submit" className="mt-6 flex w-full items-center justify-center gap-2 bg-volt py-3.5 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <><ShieldCheck size={15} /> Submit for Verification</>}
              </button>
              <p className="mt-3 text-center text-[11px] text-white/40">Documents are used only to verify your rental. No payment is taken online.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
